import React, { useEffect, useState } from "react";
import { Row, Col, Card, Button, Typography, notification, Form, Input, Select, DatePicker, InputNumber, Divider } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import { useHistory, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import { baseUrl } from "../../config";

const { Title, Text } = Typography;
const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

function FamilyMemberForm() {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const familyIdFromUrl = queryParams.get('family_id');

  const [form] = Form.useForm();
  const isEdit = !!id;

  const [familyInfo, setFamilyInfo] = useState(null);
  const [families, setFamilies] = useState([]);
  const [educations, setEducations] = useState([]);
  const [occupations, setOccupations] = useState([]);

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  useEffect(() => {
    axios.get(`${baseUrl}/pop-education/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setEducations(r.data.result); });
    axios.get(`${baseUrl}/pop-occupations/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setOccupations(r.data.result); });

    if (familyIdFromUrl) {
      axios.get(`${baseUrl}/pop-families/${familyIdFromUrl}`, { headers: authHeader })
        .then(r => { if (r.data.status) setFamilyInfo(r.data.result); });
    } else {
      axios.get(`${baseUrl}/pop-families/list-all`, { headers: authHeader })
        .then(r => { if (r.data.status) setFamilies(r.data.result); });
    }
  }, []);

  useEffect(() => {
    if (isEdit) {
      axios.get(`${baseUrl}/pop-family-member/${id}`, { headers: authHeader })
        .then(res => {
          if (res.data.status) {
            const record = res.data.result;
            form.setFieldsValue({
              ...record,
              education: record.education ? parseInt(record.education) : undefined,
              occupation: record.occupation ? parseInt(record.occupation) : undefined,
              dob: record.dob && record.dob !== '0000-00-00' ? moment(record.dob) : null,
              status: String(record.status)
            });
          }
        });
    } else if (familyIdFromUrl) {
      form.setFieldsValue({ family_id: parseInt(familyIdFromUrl) });
    }
  }, [id]);

  const onFinish = async (values) => {
    const payload = {
      ...values,
      dob: values.dob ? values.dob.format('YYYY-MM-DD') : null,
      family_id: familyIdFromUrl ? parseInt(familyIdFromUrl) : values.family_id
    };
    try {
      if (isEdit) {
        await axios.put(`${baseUrl}/pop-family-member/${id}`, payload, { headers: authHeader });
      } else {
        await axios.post(`${baseUrl}/pop-family-member`, payload, { headers: authHeader });
      }
      notification.success({ message: isEdit ? 'Member updated successfully' : 'Member added successfully' });
      const backPath = familyIdFromUrl ? `/pop-family-member?family_id=${familyIdFromUrl}` : '/pop-family-member';
      history.push(backPath);
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error saving record' });
    }
  };

  const cancelPath = familyIdFromUrl ? `/pop-family-member?family_id=${familyIdFromUrl}` : '/pop-family-member';

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <Card
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  size="small"
                  style={{ background: '#fff', borderColor: '#fff', color: '#7a0000', fontWeight: 600 }}
                  onClick={() => history.push(cancelPath)}
                >Back</Button>
                <span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>
                  {isEdit ? 'Edit Family Member' : 'Add Family Member'}
                </span>
                {familyInfo && (
                  <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 400 }}>
                    — Family: <strong style={{ color: '#fff' }}>{familyInfo.head_family}</strong>
                    {familyInfo.village_name ? ` (${familyInfo.village_name})` : ''}
                  </span>
                )}
              </div>
            }
          >
            <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ status: '1' }}>

              {/* ── Basic Info ── */}
              <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>Basic Information</Divider>

              <Row gutter={[16, 0]}>

                {/* Family selector (only if not linked from family) */}
                {!familyIdFromUrl && (
                  <Col span={24} md={12}>
                    <Item name="family_id" label="Family" rules={[{ required: true, message: 'Please select a family' }]}>
                      <Select placeholder="Select family" showSearch optionFilterProp="children">
                        {families.map(f => <Option key={f.id} value={f.id}>{f.head_family}{f.village_name ? ` (${f.village_name})` : ''}</Option>)}
                      </Select>
                    </Item>
                  </Col>
                )}

                <Col span={24} md={12}>
                  <Item name="member_name" label="Member Name" rules={[{ required: true, message: 'Please enter member name' }]}>
                    <Input placeholder="Enter member name" />
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="relation_type" label="Relation Type">
                    <Select placeholder="Select relation">
                      <Option value="son_of">Son Of</Option>
                      <Option value="wife_of">Wife Of</Option>
                      <Option value="daughter_of">Daughter Of</Option>
                      <Option value="Self">Self</Option>
                      <Option value="Father">Father</Option>
                      <Option value="Mother">Mother</Option>
                      <Option value="Brother">Brother</Option>
                      <Option value="Sister">Sister</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="relation_name_with" label="Relation Name With">
                    <Input placeholder="Enter relation name" />
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="gender_type" label="Gender">
                    <Select placeholder="Select gender">
                      <Option value="male">Male</Option>
                      <Option value="female">Female</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="married_status" label="Married Status">
                    <Select placeholder="Select married status">
                      <Option value="married">Married</Option>
                      <Option value="unmarried">UnMarried</Option>
                    </Select>
                  </Item>
                </Col>

              </Row>

              {/* ── Personal Details ── */}
              <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>Personal Details</Divider>

              <Row gutter={[16, 0]}>

                <Col span={24} md={12}>
                  <Item name="dob" label="Date of Birth">
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" placeholder="Select date of birth" />
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="age" label="Age (Years)">
                    <InputNumber style={{ width: '100%' }} min={0} max={150} placeholder="Enter age" />
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="education" label="Education">
                    <Select placeholder="Select education" showSearch optionFilterProp="children" allowClear>
                      {educations.map(e => <Option key={e.id} value={e.id}>{e.name}</Option>)}
                    </Select>
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="occupation" label="Occupation">
                    <Select placeholder="Select occupation" showSearch optionFilterProp="children" allowClear>
                      {occupations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                    </Select>
                  </Item>
                </Col>

              </Row>

              {/* ── Contact ── */}
              <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>Contact Information</Divider>

              <Row gutter={[16, 0]}>

                <Col span={24} md={12}>
                  <Item name="phone_no" label="Phone No">
                    <Input placeholder="Enter phone number" />
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="email_address" label="Email Address">
                    <Input placeholder="Enter email address" type="email" />
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="address" label="Current Address">
                    <TextArea rows={2} placeholder="Enter current address" />
                  </Item>
                </Col>

                <Col span={24} md={12}>
                  <Item name="status" label="Status">
                    <Select>
                      <Option value="1">Active</Option>
                      <Option value="0">Inactive</Option>
                    </Select>
                  </Item>
                </Col>

              </Row>

              <Divider style={{ borderColor: '#d0dce8', margin: '20px 0 16px' }} />

              <Item style={{ marginTop: 8, marginBottom: 0 }}>
                <Button
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}
                >Save Member</Button>
                <Button style={{ marginLeft: 8 }} onClick={() => history.push(cancelPath)}>Cancel</Button>
              </Item>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default FamilyMemberForm;
